import React, { useState } from "react";
import { Modal, Stack, Group, Title, Text } from "@mantine/core";
import { useMutation } from "@apollo/client";
import { gql } from "@/__generated__";
import {
  AppealOutcomeSource,
  AppealCurrentStatus,
} from "@/__generated__/graphql";
import { Button } from "./buttons/button";
import { PillRadioButton } from "./pill-radio-button";
import { Toast } from "design-system/src/components/toast";

const REPORT_APPEAL_STATUS = gql(`
  mutation ReportAppealStatus($appealId: ID!, $status: ReportAppealStatusInput!, $source: AppealOutcomeSource) {
    reportAppealOutcomeStatus(appealId: $appealId, status: $status, source: $source)
  }
`);

interface AppealOutcomeUpdateModalProps {
  onClose: () => void;
  appealId: string;
  onSuccess: () => void;
}

const OUTCOME_OPTIONS = [
  {
    value: AppealCurrentStatus.Overturned,
    label: "Appeal overturned",
    iconType: "positive" as const,
  },
  {
    value: AppealCurrentStatus.Upheld,
    label: "Appeal upheld",
    iconType: "negative" as const,
  },
];

export function AppealOutcomeUpdateModal({
  onClose,
  appealId,
  onSuccess,
}: AppealOutcomeUpdateModalProps) {
  const [selectedOutcome, setSelectedOutcome] =
    useState<AppealCurrentStatus | null>(null);

  const [reportAppealStatus, { loading: isLoading, error }] =
    useMutation(REPORT_APPEAL_STATUS);

  const handleSubmit = async () => {
    if (selectedOutcome) {
      const status =
        selectedOutcome === AppealCurrentStatus.Overturned
          ? { denialOverturned: true, denialUpheld: false }
          : { denialOverturned: false, denialUpheld: true };

      const { data, errors } = await reportAppealStatus({
        variables: {
          appealId,
          status,
          source: AppealOutcomeSource.ClaimableConsole,
        },
      });

      if (!errors && data) {
        onSuccess();
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setSelectedOutcome(null);
    onClose();
  };

  const isChanged = selectedOutcome !== null;

  return (
    <Modal
      opened={true}
      onClose={handleClose}
      title={<Title order={3}>Update Appeal Outcome</Title>}
      centered
      size="md"
    >
      <Stack gap="md">
        <Toast variant="error" isVisible={!!error}>
          {error?.message}
        </Toast>

        <Text size="sm" c="dimmed">
          What is the status of this appeal?
        </Text>

        <Stack gap="xs">
          {OUTCOME_OPTIONS.map((option) => (
            <PillRadioButton
              key={option.value}
              selected={selectedOutcome === option.value}
              iconType={option.iconType}
              onClick={() => setSelectedOutcome(option.value)}
              disabled={isLoading}
            >
              {option.label}
            </PillRadioButton>
          ))}
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button
            variant="secondary"
            size="medium"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={isLoading || !isChanged}
            loading={isLoading}
          >
            {isLoading ? "Updating..." : "Update Outcome"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
